import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import {
  KeyRound, Loader2, Plus, Search, ShieldCheck, ShieldOff, Trash2, Users, UserRoundCheck, X,
} from 'lucide-react';
import RequirePermission from '../../components/AccessControl/RequirePermission';
import AddStaffModal from '../../components/AddStaffModal/AddStaffModal';
import { SMIIFY_API } from '../../config/api';
import styles from './UserManagement.module.css';

const STAFF_API = `${SMIIFY_API}/staff`;
const LOGINS_API = `${SMIIFY_API}/access/logins`;
const ROLE_LABEL = { ADMIN: 'Admin', MANAGER: 'Manager', RECEPTIONIST: 'Receptionist', STAFF: 'Staff' };

const getId = (record) => String(record?.id || record?._id || '');
const getName = (record) => record?.name || `${record?.firstName || ''} ${record?.lastName || ''}`.trim() || 'Unnamed staff';
const getStatusLabel = (status) => {
  if (status === 'On_Leave') return 'On leave';
  if (status === 'Suspended') return 'Suspended';
  if (status === 'Terminated') return 'Terminated';
  return status || 'Inactive';
};

const UserManagement = () => {
  const { activeStore } = useStore();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const [userList, setUserList] = useState([]);
  const [branchLogins, setBranchLogins] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const storeId = activeStore?.id || activeStore?._id;
  const canCreate = hasPermission('users', 'create');
  const canEdit = hasPermission('users', 'edit');
  const canDelete = hasPermission('users', 'delete');
  const canManageAccess = hasPermission('users', 'manage_permissions');
  const showActionsColumn = canEdit || canDelete || canManageAccess;

  const fetchUserList = useCallback(async (storeIdToFetch) => {
    if (!storeIdToFetch || storeIdToFetch === 'global_hq') {
      setUserList([]);
      setBranchLogins([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setLoadError('');
    const token = localStorage.getItem('smiify_token');
    const headers = { Authorization: `Bearer ${token}`, 'x-store-id': String(storeIdToFetch) };
    try {
      const [staffRes, loginRes] = await Promise.all([
        fetch(`${STAFF_API}?storeId=${encodeURIComponent(storeIdToFetch)}`, { headers }),
        fetch(`${LOGINS_API}?storeId=${encodeURIComponent(storeIdToFetch)}`, { headers }).catch(() => null),
      ]);
      const result = await staffRes.json().catch(() => ({}));
      if (!staffRes.ok || !result.success) throw new Error(result.message || 'Unable to load staff list.');
      setUserList(Array.isArray(result.data) ? result.data : []);

      const logins = loginRes ? await loginRes.json().catch(() => ({})) : {};
      setBranchLogins(logins.success && Array.isArray(logins.data) ? logins.data.filter((l) => !l.hasStaffProfile) : []);
    } catch (error) {
      setUserList([]);
      setLoadError(error.message === 'Failed to fetch' ? 'Cannot reach the server.' : (error.message || 'Unable to load staff list.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchUserList(storeId); }, [fetchUserList, storeId]);

  const roleOptions = useMemo(() => {
    const roles = userList.map((u) => u.role).filter((r) => r && r !== 'NONE');
    return ['ALL', ...new Set(roles)];
  }, [userList]);

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return userList.filter((u) => {
      const haystack = [getName(u), u.phone, u.email, u.username, u.designation, u.role].filter(Boolean).join(' ').toLowerCase();
      return (!query || haystack.includes(query)) && (roleFilter === 'ALL' || u.role === roleFilter);
    });
  }, [roleFilter, searchTerm, userList]);

  const metrics = useMemo(() => ({
    total: userList.length,
    active: userList.filter((u) => u.status === 'Active').length,
    access: userList.filter((u) => u.hasAccess).length + branchLogins.length,
    setup: userList.filter((u) => !u.hasAccess).length,
  }), [userList, branchLogins]);

  const openCredentials = (u) => navigate(`/users/${encodeURIComponent(getId(u))}/credentials`, { state: { staff: u } });
  const openAccess = (id, staff) => navigate(`/users/${encodeURIComponent(id)}/manage-access`, { state: staff ? { staff } : undefined });

  const deactivateUser = async (u) => {
    if (!window.confirm(`Deactivate ${getName(u)}? This will remove the staff member from active branch lists.`)) return;
    const id = getId(u);
    setActionLoading(`deactivate-${id}`);
    try {
      const token = localStorage.getItem('smiify_token');
      const response = await fetch(`${STAFF_API}/${encodeURIComponent(id)}/deactivate`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'x-store-id': String(storeId) },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || 'Unable to deactivate staff member.');
      await fetchUserList(storeId);
    } catch (error) {
      setLoadError(error.message || 'Unable to deactivate staff member.');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div className={styles.pageContainer}>
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          <div className={styles.iconBox}><Users size={18} strokeWidth={2.5} /></div>
          <div>
            <h2>User Management</h2>
            <p>Manage staff profiles and branch access for {activeStore?.storeName || 'this branch'}.</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          {canCreate && (
            <RequirePermission module="users" action="create">
              <button type="button" className={styles.primaryBtn} onClick={() => setIsAddUserModalOpen(true)}>
                <Plus size={14} strokeWidth={2.5} /> Add New Staff
              </button>
            </RequirePermission>
          )}
        </div>
      </header>

      <div className={styles.contentWrapper}>
        <section className={styles.metricsGrid} aria-label="Staff summary">
          <div className={styles.metricCard}>
            <div className={styles.metricHeader}><span className={styles.metricLabel}>Total Staff</span><div className={`${styles.metricIconBg} ${styles.bgPrimary}`}><Users size={15} /></div></div>
            <div className={styles.metricValue}>{metrics.total}</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricHeader}><span className={styles.metricLabel}>Active Profiles</span><div className={`${styles.metricIconBg} ${styles.bgSuccess}`}><UserRoundCheck size={15} /></div></div>
            <div className={styles.metricValue}>{metrics.active}</div>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricHeader}><span className={styles.metricLabel}>Logins Active</span><div className={`${styles.metricIconBg} ${styles.bgBlue}`}><ShieldCheck size={15} /></div></div>
            <div className={styles.metricValue}>{metrics.access}</div>
          </div>
          <div className={`${styles.metricCard} ${metrics.setup > 0 ? styles.cardHighlight : ''}`}>
            <div className={styles.metricHeader}><span className={styles.metricLabel}>Needs Setup</span><div className={`${styles.metricIconBg} ${styles.bgWarning}`}><KeyRound size={15} /></div></div>
            <div className={styles.metricValue}>{metrics.setup}</div>
            {metrics.setup > 0 && <div className={styles.metricSub}>{metrics.setup} profiles pending</div>}
          </div>
        </section>

        {loadError && (
          <div className={styles.feedbackError} role="alert">
            <ShieldOff size={14} />
            <span>{loadError}</span>
            <button type="button" onClick={() => fetchUserList(storeId)}>Retry</button>
          </div>
        )}

        {/* Branch logins with no staff profile (e.g. made by the store activator) */}
        {branchLogins.length > 0 && (
          <div className={styles.tableContainer} style={{ marginBottom: 14 }}>
            <div className={styles.tableToolbar}>
              <strong style={{ fontSize: 12.5, fontWeight: 900 }}>Branch logins</strong>
              <span className={styles.resultCount}>Logins not linked to a staff profile</span>
            </div>
            <div className={styles.tableWrapper}>
              <table className={styles.dataTable}>
                <thead>
                  <tr><th style={{ width: '34%' }}>Login</th><th style={{ width: '18%' }}>Role</th><th style={{ width: '26%' }}>Access</th><th style={{ width: '12%' }}>Status</th>{canManageAccess && <th className={styles.textCenter}>Actions</th>}</tr>
                </thead>
                <tbody>
                  {branchLogins.map((l) => (
                    <tr key={l.loginId} className={styles.tableRow}>
                      <td>
                        <div className={styles.profileCell}>
                          <div className={styles.avatar}>{(l.name || '?').charAt(0).toUpperCase()}</div>
                          <div className={styles.profileText}><strong>{l.name}</strong><span>@{l.username}</span></div>
                        </div>
                      </td>
                      <td><span className={styles.roleBadge}>{ROLE_LABEL[l.role] || l.role}</span></td>
                      <td><span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--h-color-neutral-600)' }}>{l.usingRoleDefault ? `Default for ${ROLE_LABEL[l.role] || l.role}` : 'Custom access'}</span></td>
                      <td>
                        <div className={styles.statusCell}>
                          <span className={`${styles.statusDot} ${l.status === 'Active' ? styles.dotActive : styles.dotInactive}`} />
                          {l.status}
                        </div>
                      </td>
                      {canManageAccess && (
                        <td className={styles.textCenter}>
                          <div className={styles.actionGroup}>
                            <button type="button" className={styles.btnIcon} onClick={() => openAccess(l.loginId)} disabled={!l.canManage} title={l.canManage ? 'Manage access' : 'Only a higher role can change this login'}>
                              <ShieldCheck size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className={styles.tableContainer}>
          <div className={styles.tableToolbar}>
            <div className={styles.searchBox}>
              <Search size={14} className={styles.searchIcon} />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search name, phone, role..." autoComplete="off" className={styles.searchInput} />
              {searchTerm && <button type="button" className={styles.clearSearch} onClick={() => setSearchTerm('')}><X size={13} /></button>}
            </div>
            <div className={styles.filterGroup}>
              <span className={styles.resultCount}>{filteredUsers.length} results</span>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={styles.filterSelect}>
                {roleOptions.map((role) => <option key={role} value={role}>{role === 'ALL' ? 'All Roles' : role}</option>)}
              </select>
            </div>
          </div>

          <div className={styles.tableWrapper}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>Staff Profile</th>
                  <th style={{ width: '20%' }}>Contact Details</th>
                  <th style={{ width: '15%' }}>System Role</th>
                  <th style={{ width: '20%' }}>Dashboard Access</th>
                  <th style={{ width: '10%' }}>Status</th>
                  {showActionsColumn && <th className={styles.textCenter}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={showActionsColumn ? 6 : 5} className={styles.emptyState}><Loader2 size={24} className={styles.spinner} /><p>Loading staff records...</p></td></tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={showActionsColumn ? 6 : 5} className={styles.emptyState}>
                      <div className={styles.emptyIcon}><Users size={20} /></div>
                      <strong>{storeId === 'global_hq' ? 'Select a branch' : 'No matching staff found'}</strong>
                      <p>{storeId === 'global_hq' ? 'Users are managed per branch.' : 'Try adjusting your search or role filters.'}</p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => {
                    const id = getId(u);
                    const name = getName(u);
                    const active = u.status === 'Active';
                    const hasAccess = Boolean(u.hasAccess || u.loginAccountId || u.username);
                    const shortId = id.slice(-6).toUpperCase() || '------';
                    return (
                      <tr key={id} className={styles.tableRow}>
                        <td>
                          <div className={styles.profileCell}>
                            <div className={styles.avatar}>{name.charAt(0).toUpperCase()}</div>
                            <div className={styles.profileText}><strong>{name}</strong><span>ID: USR-{shortId}</span></div>
                          </div>
                        </td>
                        <td><div className={styles.contactCell}><strong>{u.phone || 'No phone'}</strong><span>{u.email || 'No email'}</span></div></td>
                        <td><span className={styles.roleBadge}>{u.role && u.role !== 'NONE' ? u.role : (u.designation || 'Staff')}</span></td>
                        <td>
                          <div className={`${styles.accessCell} ${hasAccess ? styles.accessGranted : styles.accessPending}`}>
                            {hasAccess ? <ShieldCheck size={14} /> : <KeyRound size={14} />}
                            <div className={styles.accessText}>
                              <strong>{hasAccess ? 'Active' : 'Needs Setup'}</strong>
                              <span>{hasAccess ? (u.username || 'Dashboard Login') : 'Create credentials'}</span>
                            </div>
                          </div>
                        </td>
                        <td><div className={styles.statusCell}><span className={`${styles.statusDot} ${active ? styles.dotActive : styles.dotInactive}`} />{getStatusLabel(u.status)}</div></td>
                        {showActionsColumn && (
                          <td className={styles.textCenter}>
                            <div className={styles.actionGroup}>
                              {canEdit && (
                                <button type="button" className={`${styles.btnIcon} ${!hasAccess ? styles.btnHighlight : ''}`} onClick={() => openCredentials(u)} title="Manage Credentials">
                                  <KeyRound size={14} />
                                </button>
                              )}
                              {canManageAccess && hasAccess && (
                                <button type="button" className={styles.btnIcon} onClick={() => openAccess(id, u)} title="Manage access">
                                  <ShieldCheck size={14} />
                                </button>
                              )}
                              {canDelete && (
                                <button type="button" className={`${styles.btnIcon} ${styles.btnDanger}`} onClick={() => deactivateUser(u)} disabled={actionLoading === `deactivate-${id}`} title="Deactivate Staff">
                                  {actionLoading === `deactivate-${id}` ? <Loader2 size={14} className={styles.spinner} /> : <Trash2 size={14} />}
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddStaffModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onStaffAdded={async () => { setIsAddUserModalOpen(false); await fetchUserList(storeId); }}
      />
    </div>
  );
};

export default UserManagement;